import React, { useState, useMemo } from 'react';
import { Github, Linkedin, Twitter, Instagram, ArrowRight, Sparkles, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { NewsletterSlider } from '../components/NewsletterSlider';
import { ProductCard } from '../components/ProductCard';
import { PatreonTier } from '../components/PatreonTier';
import { Services } from '../components/Services';
import { BlogPosts } from '../components/BlogPosts';
import { SEO, homePageJsonLd } from '../components/SEO';
import { projects } from '../data/projects';

// Fisher-Yates shuffle with seed based on date (changes daily)
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  const seed = new Date().toDateString();
  let seedNum = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    seedNum = (seedNum * 9301 + 49297) % 233280;
    const j = Math.floor((seedNum / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const Home: React.FC = () => {
  const { t } = useLanguage();
  const [productFilter, setProductFilter] = useState<string>('all');
  
  // Separate products from portfolio
  const allProducts = projects.filter(project => !project.isPortfolio && project.featured);
  const allPortfolioProjects = projects.filter(project => project.isPortfolio && project.featured);
  
  // Get unique categories for products
  const productCategories = useMemo(() => {
    const cats = [...new Set(allProducts.map(p => p.category))];
    return ['all', ...cats];
  }, []);
  
  // Filter products by category
  const filteredProducts = useMemo(() => {
    if (productFilter === 'all') return allProducts;
    return allProducts.filter(p => p.category === productFilter);
  }, [productFilter, allProducts]);
  
  // Randomize and limit portfolio to 3 items (changes daily)
  const portfolioProjects = useMemo(() => {
    return shuffleArray(allPortfolioProjects).slice(0, 3);
  }, []);

  const scrollToProducts = () => {
    const productsSection = document.getElementById('products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div>
      <SEO 
        canonical="/"
        jsonLd={homePageJsonLd}
      />
      {/* Hero Section - Product-First, Conversion Focused */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            Free tools & extensions
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            {t('hero', 'title')}
          </h1>
          <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto leading-relaxed">
            {t('hero', 'subtitle')}
          </p>

          <button
            onClick={scrollToProducts}
            className="inline-flex items-center bg-foreground text-background px-6 py-3 rounded-md font-medium hover:opacity-90 transition-opacity"
          >
            {t('promo', 'cta_button')}
            <ArrowRight className="ml-2 w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Products Section - Primary Focus */}
      {allProducts.length > 0 && (
        <section id="products" className="py-20 px-6 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="w-5 h-5 text-foreground" />
              <h2 className="text-2xl font-semibold">
                {t('products', 'title')}
              </h2>
            </div>
            <p className="text-muted-foreground mb-6">
              {t('products', 'subtitle')}
            </p>
            
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {productCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setProductFilter(cat)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    productFilter === cat
                      ? 'bg-foreground text-background'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat === 'chrome' ? 'Extensions' : cat === 'saas' ? 'SaaS' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map(project => (
                <ProductCard key={project.id} product={project} compact />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      <Services />

      {/* Portfolio Section - Secondary, Limited */}
      {portfolioProjects.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-foreground" />
                <h2 className="text-2xl font-semibold">
                  Portfolio
                </h2>
              </div>
              <a 
                href="/portfolio" 
                className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                View all <ArrowRight className="w-3 h-3" />
              </a>
            </div>
            <p className="text-muted-foreground mb-8">
              A few recent client projects.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolioProjects.map(project => (
                <ProductCard key={project.id} product={project} compact />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold mb-3">
              {t('blog', 'sectionTitle')}
            </h2>
            <p className="text-muted-foreground">
              {t('blog', 'sectionSubtitle')}
            </p>
          </div>
          <BlogPosts />
          <div className="text-center mt-10">
            <a
              href="https://blog.cemates.me"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium hover:underline underline-offset-4"
            >
              {t('blog', 'viewAll')} <ArrowRight className="ml-1 w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter Section - After showing value */}
      <NewsletterSlider />

      {/* Social Links */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm text-muted-foreground mb-6">Connect with me</p>
          <div className="flex justify-center gap-6">
            <a
              href="https://github.com/cemates"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Github className="w-5 h-5" />
            </a>
            <a
              href="https://www.linkedin.com/in/cematesh/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="https://x.com/cematesh"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Twitter className="w-5 h-5" />
            </a>
            <a
              href="https://instagram.com/cemates"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Patreon Section - At the end for supporters */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold mb-3">{t('patreon', 'support')}</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              {t('patreon', 'title')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <PatreonTier
              title={t('patreon', 'tiers.flame.title')}
              price="$1"
              description={t('patreon', 'tiers.flame.description')}
              icon="flame"
              link="https://www.patreon.com/cemates/membership"
            />
            <PatreonTier
              title={t('patreon', 'tiers.heart.title')}
              price="$4.99"
              description={t('patreon', 'tiers.heart.description')}
              icon="heart"
              link="https://www.patreon.com/cemates/membership"
            />
            <PatreonTier
              title={t('patreon', 'tiers.crown.title')}
              price="$9.99"
              description={t('patreon', 'tiers.crown.description')}
              icon="crown"
              link="https://www.patreon.com/cemates/membership"
            />
          </div>
        </div>
      </section>
    </div>
  );
};
